import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { Button } from "../components/ui/button";
import { EpcScanner } from "../components/EpcScanner";
import LoadingSpinner from "../components/LoadingSpinner";
import ReportMaintenanceButton from "../components/ReportMaintenanceButton";
import NeedsAttentionPanel from "../components/inventory/NeedsAttentionPanel";
import UnmatchedTagsPanel from "../components/inventory/UnmatchedTagsPanel";
import { useAuth } from "../contexts/AuthContext";
import { fmtUtc, fmtWorkCenter } from "../lib/format";
import {
  type InventoryItem,
  type SmartboxInventory,
  fetchInventory,
  fetchItemEpcs,
  setMinmax,
} from "../services/inventoryApi";
import { type TagEvent, fetchTagHistory, retrieveTag } from "../services/tagApi";

type Tab = "items" | "unmatched" | "attention";

/**
 * Per-smartbox inventory: matched items (grouped by SKU) with on-hand vs
 * min/max, and a collapsible Unmatched-tags panel. Data is scoped + projected
 * server-side (GET /api/boxes/:scu/inventory). Platform + vendor-admin users
 * can edit min/max (POST .../minmax → MTTR-side override).
 */
export const InventoryDetailPage: React.FC = () => {
  const { scuName } = useParams<{ scuName: string }>();
  const { token, can, role } = useAuth();
  const canEdit = can("minmax.edit");
  // Retrieve = pull a tag out of the box without a customer transaction. Same
  // capability that gates Manage Tags. Available here (#6) so an operator can
  // retrieve a tag while looking at a single box's inventory; the retrieved tag
  // drops out of on-hand (RETRIEVAL status) on reload.
  const canManageTags = can("tags.manage");
  const [reloadKey, setReloadKey] = useState(0);
  const [retrieveOpen, setRetrieveOpen] = useState(false);
  const [retrieveEpc, setRetrieveEpc] = useState("");
  const [retrieveReason, setRetrieveReason] = useState("");
  const [retrieveSaving, setRetrieveSaving] = useState(false);
  const [retrieveMsg, setRetrieveMsg] = useState<string | null>(null);
  // Drill-down: on-hand number -> EPC list -> tag history (#7).
  const [epcsFor, setEpcsFor] = useState<InventoryItem | null>(null);
  const [epcList, setEpcList] = useState<string[] | null>(null);
  const [epcsLoading, setEpcsLoading] = useState(false);
  const [historyEpc, setHistoryEpc] = useState<string | null>(null);
  const [historyEvents, setHistoryEvents] = useState<TagEvent[] | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  const openEpcs = async (it: InventoryItem): Promise<void> => {
    if (!scuName || !token) return;
    setEpcsFor(it);
    setEpcList(null);
    setEpcsLoading(true);
    try {
      const res = await fetchItemEpcs(scuName, it.item_id, token);
      setEpcList(res.epcs);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setEpcsFor(null);
    } finally {
      setEpcsLoading(false);
    }
  };

  const openHistory = async (epc: string): Promise<void> => {
    if (!token) return;
    setHistoryEpc(epc);
    setHistoryEvents(null);
    setHistoryLoading(true);
    try {
      const h = await fetchTagHistory(epc, token);
      setHistoryEvents(h.events);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setHistoryEpc(null);
    } finally {
      setHistoryLoading(false);
    }
  };
  // Min/Max are operational stocking parameters owned by platform +
  // the vendor that supplies the parts; customers shouldn't see them
  // on their own inventory view. They still see on-hand and the
  // refill-needed row highlighting (driven server-side by min/max).
  const canSeeMinMax = role === "platform" || role === "vendor";
  const [inv, setInv] = useState<SmartboxInventory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("items");
  // Free-text search over SKU + part name. Empty = show everything. Matches
  // are case-insensitive and apply to either field; rows that don't match
  // are hidden but counted in the empty-state hint.
  const [partQuery, setPartQuery] = useState("");

  // Per-row min/max edit state.
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editMin, setEditMin] = useState("");
  const [editMax, setEditMax] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const startEdit = (it: InventoryItem) => {
    setEditingId(it.item_id);
    setEditMin(it.min_qty != null ? String(it.min_qty) : "");
    setEditMax(it.suggest_qty != null ? String(it.suggest_qty) : "");
    setError(null);
  };

  const saveEdit = async (it: InventoryItem) => {
    if (!scuName || !token) return;
    const min = editMin.trim() === "" ? null : Number(editMin);
    const max = editMax.trim() === "" ? null : Number(editMax);
    if (
      (min !== null && (!Number.isInteger(min) || min < 0)) ||
      (max !== null && (!Number.isInteger(max) || max < 0))
    ) {
      setError("Min and Max must be whole numbers (0 or more), or blank.");
      return;
    }
    setSavingEdit(true);
    try {
      await setMinmax(scuName, it.item_id, min, max, token);
      setInv((prev) =>
        prev
          ? {
              ...prev,
              items: prev.items.map((x) =>
                x.item_id === it.item_id
                  ? {
                      ...x,
                      min_qty: min,
                      suggest_qty: max,
                      refill_needed: min != null && x.on_hand < min,
                      refill_qty:
                        min != null && x.on_hand < min
                          ? Math.max(0, (max ?? min) - x.on_hand)
                          : 0,
                    }
                  : x,
              ),
            }
          : prev,
      );
      setEditingId(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSavingEdit(false);
    }
  };

  useEffect(() => {
    if (!scuName || !token) return;
    let cancelled = false;
    setLoading(true);
    fetchInventory(scuName, token)
      .then((d) => {
        if (!cancelled) {
          setInv(d);
          setError(null);
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [scuName, token, reloadKey]);

  const doRetrieve = async (): Promise<void> => {
    const epc = retrieveEpc.trim();
    if (!epc || !token) return;
    setRetrieveSaving(true);
    setRetrieveMsg(null);
    try {
      const out = await retrieveTag(epc, token, retrieveReason.trim() || undefined);
      if (!out.ok) {
        setRetrieveMsg(out.reason ?? "Retrieval refused (tag not on-hand?).");
      } else {
        setRetrieveOpen(false);
        setRetrieveEpc("");
        setRetrieveReason("");
        setReloadKey((k) => k + 1); // tag now RETRIEVED → drops out of on-hand
      }
    } catch (e) {
      setRetrieveMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setRetrieveSaving(false);
    }
  };

  const refillCount = inv?.items.filter((i) => i.refill_needed).length ?? 0;

  const downloadCsv = (): void => {
    if (!inv || !scuName) return;
    // RFC 4180 escape: wrap in quotes when the cell contains comma, quote, or
    // newline; double-up internal quotes. Numbers/booleans render naturally.
    const esc = (v: string | number | boolean | null | undefined): string => {
      if (v === null || v === undefined) return "";
      const s = String(v);
      return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows: string[] = [];
    // Header section as comment-ish lines (Excel keeps them in row A).
    rows.push(`# Inventory export for ${scuName}`);
    rows.push(`# Generated: ${new Date().toISOString()}`);
    rows.push(`# Latest scan: ${inv.scan_end_utc ?? ""}`);
    rows.push(
      `# Tags: ${inv.total_tags ?? 0} total, ${inv.matched_tag_count} matched, ${inv.unmatched_count} unmatched`,
    );
    rows.push("");
    // Matched-items table. Customers don't get Min/Max in the export
    // — same gate as the on-screen columns.
    const header = ["SKU", "Name", "On hand"];
    if (canSeeMinMax) header.push("Min", "Max");
    header.push("Refill needed", "Refill qty");
    rows.push(header.map(esc).join(","));
    for (const it of inv.items) {
      const cells = [
        esc(it.sku ?? `#${it.item_id}`),
        esc(it.name),
        esc(it.on_hand),
      ];
      if (canSeeMinMax) {
        cells.push(esc(it.min_qty), esc(it.suggest_qty));
      }
      cells.push(esc(it.refill_needed ? "yes" : ""), esc(it.refill_qty || ""));
      rows.push(cells.join(","));
    }
    // Unmatched EPCs — separate section so consumers can ignore it if they
    // only want the SKU view. Empty when there are no unmatched tags.
    if (inv.unmatched.length > 0) {
      rows.push("");
      rows.push("# Unmatched EPCs (no catalog mapping in the latest scan)");
      rows.push("EPC");
      for (const epc of inv.unmatched) rows.push(esc(epc));
    }
    // Excel reads UTF-8 reliably only with a BOM; the prefix is invisible to
    // every other CSV reader.
    const blob = new Blob(["﻿" + rows.join("\r\n") + "\r\n"], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const date = new Date().toISOString().slice(0, 10);
    a.download = `inventory-${scuName}-${date}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4 md:p-6">
      <header className="mb-4 flex items-start justify-between gap-3">
        <div>
          <Link to="/inventory" className="text-xs text-primary-500 hover:underline">
            ← all smartboxes
          </Link>
          <h1 className="text-xl font-bold mt-1">
            Inventory ·{" "}
            <span className="text-secondary-900">
              {inv?.smartbox_code ?? "—"}
            </span>
            {inv?.work_center && (
              <span className="ml-2 text-secondary-600 font-normal text-base">
                · {fmtWorkCenter(inv.work_center)}
              </span>
            )}
            {inv?.customer && (
              <span className="ml-2 text-secondary-500 font-normal text-base">
                · {inv.customer}
              </span>
            )}
          </h1>
          <div className="text-[11px] text-secondary-500 font-mono mt-0.5">
            {scuName}
          </div>
        {inv && inv.scan_id !== null && (
          <div className="text-xs text-secondary-500 mt-1">
            Latest scan {fmtUtc(inv.scan_end_utc)} · {inv.total_tags} tags (
            {inv.matched_tag_count} matched, {inv.unmatched_count} unmatched)
            {refillCount > 0 && (
              <span className="ml-2 text-error-700 font-semibold">
                · {refillCount} item{refillCount === 1 ? "" : "s"} below minimum
              </span>
            )}
          </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {inv && inv.scan_id !== null && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={downloadCsv}
              title="Download this smartbox's matched items + unmatched EPCs as CSV"
            >
              Download CSV
            </Button>
          )}
          {canManageTags && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => { setRetrieveMsg(null); setRetrieveOpen(true); }}
              title="Retrieve a tag from this smartbox (removes it from on-hand)"
            >
              Retrieve tag
            </Button>
          )}
          {scuName && (
            <ReportMaintenanceButton scu={scuName} label={scuName} variant="button" />
          )}
        </div>
      </header>

      {retrieveOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-4">
            <h2 className="text-base font-semibold mb-1">Retrieve a tag</h2>
            <p className="text-xs text-secondary-500 mb-3">
              Scan or enter the tag's EPC to pull it from <strong>{scuName}</strong>{" "}
              without a customer transaction. A manual RETRIEVAL is recorded and the
              tag drops out of on-hand.
            </p>
            <EpcScanner onEpc={(e) => setRetrieveEpc(e)} />
            <input
              type="text"
              value={retrieveEpc}
              onChange={(e) => setRetrieveEpc(e.target.value.toUpperCase())}
              placeholder="EPC (scan above or type)"
              className="mt-2 w-full border border-secondary-300 rounded px-2 py-2 text-sm font-mono"
            />
            <input
              type="text"
              value={retrieveReason}
              onChange={(e) => setRetrieveReason(e.target.value)}
              placeholder="Reason (optional)"
              className="mt-2 w-full border border-secondary-300 rounded px-2 py-2 text-sm"
            />
            {retrieveMsg && (
              <div className="mt-2 text-xs text-error-700 bg-error-50 px-2 py-1 rounded">
                {retrieveMsg}
              </div>
            )}
            <div className="mt-3 flex justify-end gap-2">
              <Button
                type="button" variant="outline" size="sm" disabled={retrieveSaving}
                onClick={() => setRetrieveOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="button" size="sm"
                disabled={retrieveSaving || !retrieveEpc.trim()}
                onClick={() => void doRetrieve()}
              >
                {retrieveSaving ? "Retrieving…" : "Retrieve"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Drill-down: EPCs behind an item's on-hand count (#7) */}
      {epcsFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-4 max-h-[80vh] flex flex-col">
            <div className="flex items-baseline justify-between gap-2 mb-1">
              <h2 className="text-base font-semibold">
                On-hand tags · {epcsFor.sku ?? `#${epcsFor.item_id}`}
              </h2>
              <button
                type="button" className="text-secondary-500 hover:text-secondary-800"
                onClick={() => { setEpcsFor(null); setEpcList(null); }}
              >✕</button>
            </div>
            <p className="text-xs text-secondary-500 mb-2">
              {epcsFor.name ?? ""}{" "}
              {canManageTags && <span>· click a tag for its history</span>}
            </p>
            {epcsLoading ? (
              <div className="py-6 flex justify-center"><LoadingSpinner /></div>
            ) : !epcList || epcList.length === 0 ? (
              <div className="text-sm text-secondary-500">No tags on hand.</div>
            ) : (
              <ul className="overflow-auto divide-y divide-secondary-100 border border-secondary-100 rounded text-sm font-mono">
                {epcList.map((epc) => (
                  <li key={epc} className="px-2 py-1.5 flex items-center justify-between gap-2">
                    <span>{epc}</span>
                    {canManageTags && (
                      <button
                        type="button"
                        className="text-primary-700 hover:underline text-xs font-sans"
                        onClick={() => void openHistory(epc)}
                      >
                        history
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Tag history timeline for a drilled-into EPC */}
      {historyEpc && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-4 max-h-[80vh] flex flex-col">
            <div className="flex items-baseline justify-between gap-2 mb-2">
              <h2 className="text-base font-semibold font-mono">{historyEpc}</h2>
              <button
                type="button" className="text-secondary-500 hover:text-secondary-800"
                onClick={() => { setHistoryEpc(null); setHistoryEvents(null); }}
              >✕</button>
            </div>
            {historyLoading ? (
              <div className="py-6 flex justify-center"><LoadingSpinner /></div>
            ) : !historyEvents || historyEvents.length === 0 ? (
              <div className="text-sm text-secondary-600">No events recorded for this tag.</div>
            ) : (
              <ol className="overflow-auto border-l border-secondary-200 ml-2">
                {historyEvents.map((ev, i) => {
                  const kindLabel =
                    ev.kind === "DEPARTURE_VOID" ? "VOIDED DEPARTURE" : ev.kind;
                  const voided = ev.kind === "DEPARTURE_VOID";
                  return (
                    <li key={i} className="relative pl-4 pb-3">
                      <span className="absolute -left-1.5 top-1.5 w-3 h-3 rounded-full bg-white border border-secondary-300" />
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className={
                          "text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wide " +
                          (voided ? "bg-error-100 text-error-700 line-through" : "bg-secondary-100 text-secondary-700")
                        }>
                          {kindLabel}
                        </span>
                        <span className="text-xs text-secondary-700">
                          {ev.event_time_utc ? fmtUtc(ev.event_time_utc) : "—"}
                        </span>
                      </div>
                      {ev.notes && (
                        <div className="text-xs text-secondary-600 mt-0.5">{ev.notes}</div>
                      )}
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="mb-3 text-sm text-error-700 bg-error-50 px-3 py-2 rounded">
          {error}
        </div>
      )}

      {loading ? (
        <LoadingSpinner label="loading inventory" />
      ) : !inv || inv.scan_id === null ? (
        <div className="text-sm text-secondary-500 bg-white p-6 rounded-md border border-secondary-200">
          No scan data for this smartbox yet.
        </div>
      ) : (
        <>
          {/* Tab bar. Items / Unmatched / Needs Attention. Counts in the
              labels come from the inventory payload (unmatched) and a
              dedicated /needs-attention endpoint (loaded lazily by the
              panel component so switching tabs is fast). */}
          <div className="mb-4 border-b border-secondary-200 flex gap-1">
            {([
              ["items",     `Items (${inv.items.length})`],
              ["unmatched", `Unmatched EPCs (${inv.unmatched_count})`],
              ["attention", `Needs Attention`],
            ] as const).map(([k, label]) => {
              const active = tab === k;
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => setTab(k)}
                  className={
                    "px-3 py-2 -mb-px text-sm border-b-2 " +
                    (active
                      ? "border-primary-600 text-secondary-900 font-semibold"
                      : "border-transparent text-secondary-500 hover:text-secondary-800")
                  }
                >
                  {label}
                </button>
              );
            })}
          </div>

          {tab === "items" && (
          <>
          <div className="mb-3 flex items-center gap-2">
            <input
              type="search"
              value={partQuery}
              onChange={(e) => setPartQuery(e.target.value)}
              placeholder="Search this box by SKU or name…"
              className="flex-1 max-w-md text-sm border border-secondary-300 rounded px-2 py-1.5"
            />
            {partQuery && (
              <button
                type="button"
                onClick={() => setPartQuery("")}
                className="text-xs text-secondary-500 hover:underline"
              >
                clear
              </button>
            )}
          </div>
          {(() => {
            // Case-insensitive substring match on either field. Computed
            // inline so the renderer below can read both the filtered list
            // and the unfiltered total for the empty-state hint.
            const q = partQuery.trim().toLowerCase();
            const filtered = q
              ? inv.items.filter(
                  (it) =>
                    (it.sku ?? "").toLowerCase().includes(q) ||
                    (it.name ?? "").toLowerCase().includes(q),
                )
              : inv.items;
            return (
          <div className="bg-white border border-secondary-200 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-secondary-50 text-secondary-600 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-3 py-2">Part / SKU</th>
                  <th className="text-right px-3 py-2">On hand</th>
                  {canSeeMinMax && <th className="text-right px-3 py-2">Min</th>}
                  {canSeeMinMax && <th className="text-right px-3 py-2">Max</th>}
                  {canEdit && <th className="text-right px-3 py-2">Edit</th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map((it) => (
                  <tr
                    key={it.item_id}
                    className={
                      "border-t border-secondary-100 " +
                      (it.refill_needed ? "bg-error-50" : "")
                    }
                  >
                    <td className="px-3 py-2">
                      <div className="font-mono text-secondary-900">
                        {it.sku ?? `#${it.item_id}`}
                      </div>
                      {it.name && (
                        <div className="text-xs text-secondary-500">
                          {it.name}
                        </div>
                      )}
                    </td>
                    <td className="text-right px-3 py-2 font-semibold">
                      {it.on_hand > 0 ? (
                        <button
                          type="button"
                          onClick={() => void openEpcs(it)}
                          className="text-primary-700 hover:underline"
                          title="Show the RFID tags making up this on-hand count"
                        >
                          {it.on_hand}
                        </button>
                      ) : (
                        it.on_hand
                      )}
                    </td>
                    {editingId === it.item_id ? (
                      <>
                        <td className="text-right px-3 py-2">
                          <input
                            type="number"
                            min={0}
                            value={editMin}
                            onChange={(e) => setEditMin(e.target.value)}
                            className="w-16 border border-secondary-300 rounded px-1 py-0.5 text-right"
                          />
                        </td>
                        <td className="text-right px-3 py-2">
                          <input
                            type="number"
                            min={0}
                            value={editMax}
                            onChange={(e) => setEditMax(e.target.value)}
                            className="w-16 border border-secondary-300 rounded px-1 py-0.5 text-right"
                          />
                        </td>
                        <td className="text-right px-3 py-2 whitespace-nowrap">
                          <Button
                            type="button"
                            size="sm"
                            className="h-6 px-2 text-xs"
                            disabled={savingEdit}
                            onClick={() => void saveEdit(it)}
                          >
                            {savingEdit ? "…" : "Save"}
                          </Button>
                          <button
                            type="button"
                            className="ml-1 text-xs text-secondary-500 hover:underline"
                            onClick={() => setEditingId(null)}
                          >
                            cancel
                          </button>
                        </td>
                      </>
                    ) : (
                      <>
                        {canSeeMinMax && (
                          <td className="text-right px-3 py-2 text-secondary-600">
                            {it.min_qty ?? "—"}
                          </td>
                        )}
                        {canSeeMinMax && (
                          <td className="text-right px-3 py-2 text-secondary-600">
                            {it.suggest_qty ?? "—"}
                          </td>
                        )}
                        {canEdit && (
                          <td className="text-right px-3 py-2">
                            <button
                              type="button"
                              className="text-xs text-primary-600 hover:underline"
                              onClick={() => startEdit(it)}
                            >
                              edit
                            </button>
                          </td>
                        )}
                      </>
                    )}
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td
                      colSpan={
                        2 /* Part + On hand */
                        + (canSeeMinMax ? 2 : 0)
                        + (canEdit ? 1 : 0)
                      }
                      className="px-3 py-6 text-center text-secondary-500"
                    >
                      {inv.items.length === 0
                        ? "No matched items in the latest scan."
                        : `No items match "${partQuery}" on this box (${inv.items.length} total).`}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
            );
          })()}
          </>
          )}

          {tab === "unmatched" && scuName && (
            <UnmatchedTagsPanel
              scu={scuName}
              unmatched={inv.unmatched}
              unmatchedCount={inv.unmatched_count}
            />
          )}

          {tab === "attention" && scuName && (
            <NeedsAttentionPanel scu={scuName} />
          )}
        </>
      )}

    </div>
  );
};

export default InventoryDetailPage;
